import React, { useEffect, useState } from "react";
import {
  cancelFiscalTicket,
  downloadFiscalBackup,
  getFiscalTicketHistory,
} from "../../api/fiscal";

// HELPERS
import { ERROR_MESSAGE_SUPPORT } from "../../common/helpersTxt";
// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
// COLORS
import { grey } from "@mui/material/colors";

// APP COMPONENTS
import DateFilter from "../search/dateFilter";
import ConfirmModal from "../modals/confirmModal";
import SearchFilter from "../search/searchFilter";
import CollapseAlert from "../alerts/CollapseAlert";
import FiscalTicketRow from "./table/FiscalTicketRow";
import FiscalInfoModal from "../orders/fiscal/FiscalInfoModal";
import FiscalTicketTypeFilter from "../search/fiscalTicketTypeFilter";
import { getStartAndEndOfDay } from "../../extras";
import { setDateRange } from "../../utils/filters";
import PageHeaderLight from "../ui/PageHeaderLight";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import Paper from "@mui/material/Paper";
import { alpha, useTheme } from "@mui/material";
import BackupDownloadModal from "./modal/BackupDownloadModal";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

export default function FiscalTickets() {
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [fiscalTickets, setFiscalTickets] = useState([]);

  const [showFiscalTicketInfoModal, setShowFiscalTicketInfoModal] =
    useState(null);
  const [fiscalTicketToShow, setFiscalTicketToShow] = useState(null);

  const [showFiscalTicketCancelModal, setShowFiscalTicketCancelModal] =
    useState(null);
  const [fiscalTicketToCancel, setFiscalTicketToCancel] = useState(null);

  const [showBackupDownloadModal, setShowBackupDownloadModal] = useState(false);

  const [cancelReason, setCancelReason] = useState("");

  const [filters, setFilters] = useState({
    type: "ALL",
    newSearch: false,
    dateStart: getStartAndEndOfDay().dateStart,
    dateEnd: getStartAndEndOfDay().dateEnd,
  });
  const [showButtonMore, setShowButtonMore] = useState(true);
  const [filtersVisible, setFiltersVisible] = useState(true);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    loadFiscalTickets(true).then((r) => r);
  }, []);

  const toggleFilters = () => {
    setFiltersVisible(!filtersVisible);
  };

  const handleOpenBackupDownloadModal = () => {
    setShowBackupDownloadModal(true);
  };

  const handleCloseBackupDownloadModal = () => {
    setShowBackupDownloadModal(false);
  };

  const handleDownloadBackup = async (year, month) => {
    try {
      const { data } = await downloadFiscalBackup(year, month);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const showErrorMessage = (message = null) => {
    setOpenErrorAlert(true);
    setErrorMessage(message || ERROR_MESSAGE_SUPPORT);
  };

  const switchShowButtonMore = (length) => {
    if (length > 0) {
      setShowButtonMore(true);
      return;
    }

    setShowButtonMore(false);
  };

  const search = async (filtersData) => {
    try {
      const filtersObj = setDateRange(
        filtersData,
        DateFilter.name,
        { useISOFormat: true },
        {
          newSearch: true,
          type: filtersData[FiscalTicketTypeFilter.name]?.value ?? "ALL",
        }
      );

      setFilters(filtersObj);
      setPage(1);

      await loadFiscalTickets(true, filtersObj);
    } catch (e) {}
  };

  const loadFiscalTickets = async (newSearch, filtersData = null) => {
    try {
      setLoading(true);

      const dateStart = filters.dateStart
        ? filters.dateStart
        : getStartAndEndOfDay().dateStart;
      const dateEnd = filters.dateEnd
        ? filters.dateEnd
        : getStartAndEndOfDay().dateEnd;

      let pageQuery = page;
      if (newSearch) {
        pageQuery = 1;
        setPage(1);
      }

      let params = {
        page: pageQuery,
        type: filters.type,
        start_date: dateStart,
        end_date: dateEnd,
      };

      if (filtersData) {
        params = {
          page: pageQuery,
          type: filtersData.type ?? "ALL",
          start_date:
            filtersData.dateStart
              ? filtersData.dateStart
              : new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
          end_date:
            filtersData.dateEnd
              ? filtersData.dateEnd
              : new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
        };
      }

      const { data } = await getFiscalTicketHistory(params);

      let result;
      if (newSearch) {
        result = [...data];
        switchShowButtonMore(data.length);
      } else {
        const newFiscalTickets = data.filter(
          ({ id: id1 }) => !fiscalTickets.some(({ id: id2 }) => id2 === id1)
        );
        result = [...fiscalTickets, ...newFiscalTickets];
        switchShowButtonMore(newFiscalTickets.length);
      }

      setPage(pageQuery + 1);
      setFiscalTickets(result);
      setLoading(false);
    } catch (e) {
      showErrorMessage(e.response?.data?.error || ERROR_MESSAGE_SUPPORT);
      setLoading(false);
    }
  };

  const handleShowFiscalTicketInfoModal = (fiscalTicket = null) => {
    setFiscalTicketToShow(fiscalTicket);
    setShowFiscalTicketInfoModal(!showFiscalTicketInfoModal);
  };

  const handleShowFiscalTicketCancelModal = (fiscalTicket = null) => {
    setFiscalTicketToCancel(fiscalTicket);
    setShowFiscalTicketCancelModal(!showFiscalTicketCancelModal);
  };

  const handleCancelFiscalTicket = async () => {
    try {
      setLoading(true);
      setShowFiscalTicketCancelModal(false);

      const params = {
        fid: fiscalTicketToCancel.id,
        reason: cancelReason,
      };

      const res = await cancelFiscalTicket(params);

      if (res.data.status !== "Cancelado") {
        showErrorMessage(res.data.mensagem_sefaz);
      }

      await loadFiscalTickets(true);
    } catch (e) {
      showErrorMessage(e.response?.data?.error || ERROR_MESSAGE_SUPPORT);
    }
  };

  const pageHeaderActions = [
    {
      key: "download-backup",
      variant: "outlined",
      startIcon: <CloudDownloadIcon />,
      onClick: handleOpenBackupDownloadModal,
      children: "Baixar Backup Mensal",
    },
    {
      key: "filtros",
      variant: "outlined",
      startIcon: <FilterListIcon />,
      onClick: toggleFilters,
      children: "Filtros",
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 4, md: 6 }, maxWidth: "1800px", mx: "auto" }}>
      <PageHeaderLight
        title="Notas Fiscais"
        subtitle="Histórico de Notas Fiscais"
        actions={pageHeaderActions}
        filters={
          <SearchFilter
            title=""
            filters={[FiscalTicketTypeFilter]}
            filterDate
            shouldCenter
            submit={search}
            initialExpanded
            showHeader={false}
          />
        }
        expandedFilters={filtersVisible}
        toggleFilters={toggleFilters}
      />

      <Box>
        <CollapseAlert
          errorMessage={errorMessage}
          openErrorAlert={openErrorAlert}
          setOpenErrorAlert={setOpenErrorAlert}
          severity="error"
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {loading ? (
              <Box sx={{ display: "flex", height: "50%" }}>
                <CircularProgress
                  sx={{ margin: "auto", "&:focus-visible": { border: "none" } }}
                />
              </Box>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >
                {fiscalTickets.length === 0 && (
                  <Typography variant="body2" align="center" gutterBottom>
                    Nenhum dado fiscal encontrado
                  </Typography>
                )}

                {fiscalTickets &&
                  fiscalTickets.map((fiscalTicket, index) => {
                    return (
                      <FiscalTicketRow
                        key={fiscalTicket.id}
                        id={fiscalTicket.id}
                        index={index + 1}
                        orderIndex={fiscalTicket.order_index}
                        orderChannel={fiscalTicket.order_channel}
                        total={fiscalTicket.total}
                        status={fiscalTicket.status}
                        type={fiscalTicket.ticket_type}
                        recipient_identification={
                          fiscalTicket.recipient_identification
                        }
                        seeDetails={() =>
                          handleShowFiscalTicketInfoModal(fiscalTicket)
                        }
                        deleteAction={() =>
                          handleShowFiscalTicketCancelModal(fiscalTicket)
                        }
                      />
                    );
                  })}
                <Box
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    display: "flex",
                  }}
                >
                  <Button
                    title="Carregar mais"
                    size="large"
                    disabled={!showButtonMore}
                    fullWidth
                    sx={{
                      borderTop: `1px solid ${grey[200]}`,
                      color: grey[800],
                    }}
                    startIcon={<ExpandCircleDownIcon fontSize="inherit" />}
                    onClick={() => loadFiscalTickets(false)}
                  >
                    Mostrar mais
                  </Button>
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>

      {showFiscalTicketInfoModal && (
        <FiscalInfoModal
          show={showFiscalTicketInfoModal}
          orderId={fiscalTicketToShow.order}
          closeModal={handleShowFiscalTicketInfoModal}
        />
      )}

      {showFiscalTicketCancelModal && (
        <ConfirmModal
          show={showFiscalTicketCancelModal}
          action={handleCancelFiscalTicket}
          label={"ATENÇÃO"}
          message={"Deseja cancelar a nota fiscal selecionada?"}
          needConfirmationText
          confirmationText={cancelReason}
          setConfirmationText={setCancelReason}
          closeModal={handleShowFiscalTicketCancelModal}
        />
      )}

      {showBackupDownloadModal && (
        <BackupDownloadModal
          open={showBackupDownloadModal}
          onClose={handleCloseBackupDownloadModal}
          onDownload={handleDownloadBackup}
        />
      )}
    </Box>
  );
}
